// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Sign-in and membership as data (ruxd server mode, phase S3): where the
 * login page sends a browser, what each refusal says, what a role may do.
 * Kept free of React so it is unit-tested in Node.
 */

import { ApiRequestError } from '../api/client';
import { SupabaseAuthError } from '../api/supabase';
import type { ApiToken, AuthMe, AuthUser, CaseMember, CaseRole, GlobalRole, ShareRole } from '../api/types';
import { CASES_PATH, shareHref } from './cases';
import { BridgeRefusedError, CookieNotStoredError } from './session';

/** The login page. Outside every case, like the case list. */
export const LOGIN_PATH = '/login';

/** What the page should do once `GET /auth/me` has answered. */
export type AuthBoot =
  | { kind: 'proceed'; me: AuthMe }
  /** Nobody is signed in: to the login page, coming back to `next`. */
  | { kind: 'login'; href: string }
  /** The server could not answer: show the error, offer a retry. */
  | { kind: 'error' };

export function authBootAction(
  result: { ok: true; me: AuthMe } | { ok: false; status?: number },
  here: string,
  /** The share link this tab came in through: its visitor has no login, so back to the link. */
  shareToken: string | null = null,
): AuthBoot {
  if (result.ok) return { kind: 'proceed', me: result.me };
  if (result.status === 401) return { kind: 'login', href: shareToken ? shareHref(shareToken) : loginHref(here) };
  return { kind: 'error' };
}

/** The login page, set to come back to `here` (path + query). */
export function loginHref(here: string): string {
  const next = safeNext(here);
  return next === CASES_PATH ? LOGIN_PATH : `${LOGIN_PATH}?next=${encodeURIComponent(next)}`;
}

/**
 * Where a successful login goes: `next` when it is strictly a path on this
 * server, else the case list. A strict allowlist rather than a blocklist
 * (S3 review I1 — the URL parser strips tab and newline, so "/\t/evil.com"
 * resolved off-site):
 *  - it starts with exactly one `/`, not followed by `/` or `\`;
 *  - neither it nor its percent-decoding holds a control character or a
 *    backslash, and its decoding does not start with `//`;
 *  - resolved against this origin, it stays on this origin;
 *  - it is not the login page itself.
 */
export function safeNext(next: string | null | undefined): string {
  if (!next || !isStrictLocalPath(next)) return CASES_PATH;
  let decoded: string;
  try {
    decoded = decodeURIComponent(next);
  } catch {
    return CASES_PATH;
  }
  if (!isStrictLocalPath(decoded)) return CASES_PATH;
  const base = 'http://rux.invalid';
  let resolved: URL;
  try {
    resolved = new URL(next, base);
  } catch {
    return CASES_PATH;
  }
  if (resolved.origin !== base) return CASES_PATH;
  if (resolved.pathname === LOGIN_PATH || resolved.pathname.startsWith(`${LOGIN_PATH}/`)) return CASES_PATH;
  return next;
}

// eslint-disable-next-line no-control-regex
const CONTROL_OR_BACKSLASH = /[\u0000-\u001f\u007f\\]/;

function isStrictLocalPath(path: string): boolean {
  return /^\/(?![/\\])/.test(path) && !CONTROL_OR_BACKSLASH.test(path);
}

/** The Danish line under a refused login (Supabase, then the bridge to ruxd). */
export function loginErrorText(cause: unknown): string {
  if (cause instanceof CookieNotStoredError) return cause.message;
  if (cause instanceof BridgeRefusedError)
    return 'Kontoen har ikke adgang til serveren. Kontakt serverens administrator.';
  if (cause instanceof SupabaseAuthError) {
    const code = cause.code ?? '';
    if (code === 'invalid_credentials' || code === 'validation_failed' || (cause.status === 400 && !code))
      return 'Forkert e-mail eller adgangskode.';
    if (code === 'email_not_confirmed') return 'E-mailen er ikke bekræftet endnu. Tjek din indbakke.';
    if (code === 'user_banned') return 'Kontoen er spærret. Kontakt serverens administrator.';
    if (cause.status === 429 || code.startsWith('over_')) return 'For mange forsøg. Vent lidt, og prøv igen.';
    if (cause.status !== undefined && cause.status >= 500)
      return 'Login-tjenesten svarer ikke lige nu. Prøv igen om lidt.';
    if (cause.status !== undefined) return 'Login blev afvist. Prøv igen.';
  }
  if (cause instanceof ApiRequestError) {
    if (cause.status === 403) return 'Serveren afviste forespørgslen (oprindelse ikke tilladt).';
    if (cause.status >= 500) return 'Serveren kan ikke logge ind lige nu. Prøv igen om lidt.';
    if (cause.status === 400) return 'Serveren forstod ikke login-forespørgslen.';
  }
  return 'Kunne ikke nå serveren.';
}

/** Why the login page stopped forwarding a signed-in browser (`loginLoop.ts`). */
export const LOGIN_LOOP_TEXT =
  'Du blev sendt tilbage til login flere gange i træk, så siden stoppede. Browseren gemmer sandsynligvis ikke login-cookien (siden skal åbnes over HTTPS). Log ind igen, eller kontakt serverens administrator.';

/**
 * Whether a refused login-link request should still read as "sent": GoTrue
 * answers an unknown address differently, and saying so would tell anyone
 * which e-mails have an account.
 */
export function loginLinkLooksSent(cause: unknown): boolean {
  return (
    cause instanceof SupabaseAuthError &&
    ['otp_disabled', 'signup_disabled', 'user_not_found', 'email_address_not_authorized'].includes(cause.code ?? '')
  );
}

/** The Danish line under a refused login-link request. */
export function loginLinkErrorText(cause: unknown): string {
  if (cause instanceof SupabaseAuthError) {
    if (cause.status === 429 || (cause.code ?? '').startsWith('over_'))
      return 'Der er sendt for mange links. Vent lidt, og prøv igen.';
    if (cause.code === 'email_address_invalid' || cause.code === 'validation_failed') return 'Skriv en gyldig e-mail.';
    if (cause.status !== undefined && cause.status >= 500)
      return 'Der kunne ikke sendes et link lige nu. Prøv igen om lidt.';
  }
  return 'Kunne ikke nå login-tjenesten.';
}

/** Where a login link comes back to: the login page, keeping `next`. */
export function loginLinkRedirect(origin: string, next: string): string {
  return origin.replace(/\/+$/, '') + loginHref(next);
}

/** Danish names of the roles, as the UI shows them. */
export const ROLE_LABELS: Record<CaseRole, string> = {
  owner: 'Ejer',
  editor: 'Redaktør',
  viewer: 'Læser',
};

/** What each role may do, in one line, for the role picker. */
export const ROLE_HINTS: Record<CaseRole, string> = {
  owner: 'Alt, også at slette sagen og styre medlemmer',
  editor: 'Kan ændre sagen, men ikke slette den eller styre medlemmer',
  viewer: 'Kan kun se',
};

export const ROLES: readonly CaseRole[] = ['owner', 'editor', 'viewer'];

/** Danish names of the account-wide roles (ruxd-wmc.1). */
export const GLOBAL_ROLE_LABELS: Record<GlobalRole, string> = {
  user: 'Bruger',
  group_manager: 'Gruppeadministrator',
  admin: 'Administrator',
};

export const GLOBAL_ROLE_HINTS: Record<GlobalRole, string> = {
  user: 'Ser de sager, den får adgang til, og kan oprette sager',
  group_manager: 'Kan også oprette grupper og styre, hvem der er med',
  admin: 'Alt: alle sager, alle teams, brugere og roller',
};

export const GLOBAL_ROLES: readonly GlobalRole[] = ['user', 'group_manager', 'admin'];

/** What a case's share with a group gives its members: the case roles' own names (ruxd-wmc.3). */
export const SHARE_ROLE_LABELS: Record<ShareRole, string> = {
  editor: ROLE_LABELS.editor,
  viewer: ROLE_LABELS.viewer,
};

export const SHARE_ROLES: readonly ShareRole[] = ['editor', 'viewer'];

/**
 * The account-wide role, also from a server that predates roles (it sends
 * only `is_admin`).
 */
export function globalRoleOf(user: Pick<AuthUser, 'is_admin'> & { role?: GlobalRole }): GlobalRole {
  return user.role ?? (user.is_admin ? 'admin' : 'user');
}

/** Whether `role` may create, rename and delete groups and add or remove their members. */
export function canManageGroups(role: GlobalRole | undefined): boolean {
  return role === 'group_manager' || role === 'admin';
}

/** Whether `role` may add, change and remove members. */
export function canManageMembers(role: CaseRole | null | undefined): boolean {
  return role === 'owner';
}

/** Whether `role` may change anything in the case. */
export function canEdit(role: CaseRole | null | undefined): boolean {
  return role === 'owner' || role === 'editor';
}

/** Members, owners first, then by name. */
export function sortMembers(members: readonly CaseMember[]): CaseMember[] {
  const rank: Record<CaseRole, number> = { owner: 0, editor: 1, viewer: 2 };
  return [...members].sort(
    (a, b) =>
      rank[a.role] - rank[b.role] ||
      (a.user.display_name || a.user.email).localeCompare(b.user.display_name || b.user.email, 'da'),
  );
}

/**
 * Whether removing or demoting `member` would leave the case without an
 * owner (the server refuses it with 409; the UI disables it first).
 */
export function isLastOwner(member: CaseMember, members: readonly CaseMember[]): boolean {
  return member.role === 'owner' && members.filter((m) => m.role === 'owner').length <= 1;
}

/** What a membership change was, for its error line. */
export type MemberAction = 'add' | 'role' | 'remove';

/**
 * The Danish line under a refused membership change. Adding is by e-mail
 * (`POST /cases/{cid}/members {email, role}`): its 404 means no account has
 * that address — accounts are made in Supabase, not here.
 */
export function memberErrorText(cause: unknown, action?: MemberAction): string {
  if (cause instanceof ApiRequestError) {
    if (cause.status === 404 && (action === 'add' || /email|user/i.test(cause.message)))
      return 'Der er ingen bruger med den e-mail. En administrator opretter brugere.';
    if (cause.status === 404) return 'Medlemmet findes ikke længere.';
    if (cause.status === 409 && /owner/i.test(cause.message))
      return 'En sag skal have mindst én ejer. Gør en anden til ejer først.';
    if (cause.status === 409) return 'Brugeren er allerede medlem. Skift rollen i stedet.';
    if (cause.status === 403) return 'Kun sagens ejere kan styre medlemmer.';
    if (cause.status === 400) return 'Skriv en gyldig e-mail.';
  }
  return 'Ændringen kunne ikke gemmes.';
}

/**
 * The address to add a member by: trimmed and lower-cased (Supabase stores
 * e-mails lower-cased), or null when it cannot be an e-mail at all.
 */
export function normalizeMemberEmail(raw: string): string | null {
  const email = raw.trim().toLowerCase();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : null;
}

/** The initials shown on the user button: "Anna Berg" → "AB". */
export function initials(name: string): string {
  const parts = name
    .replace(/@.*$/, '')
    .split(/[\s._-]+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  const letters = parts.length === 1 ? parts[0].slice(0, 2) : parts[0][0] + parts[parts.length - 1][0];
  return letters.toUpperCase();
}

/** The expiry choices for a new API token (days; 0 = never). */
export const TOKEN_LIFETIMES: readonly { days: number; label: string }[] = [
  { days: 30, label: '30 dage' },
  { days: 90, label: '90 dage' },
  { days: 365, label: '1 år' },
  { days: 0, label: 'Udløber aldrig' },
];

/** A token's line under its name: scope, expiry and last use, in Danish. */
export function tokenLine(t: ApiToken): string {
  const day = (iso: string) => iso.slice(0, 10);
  return [
    t.case ? `kun sagen ${t.case}` : 'alle dine sager',
    t.expires_at ? `udløber ${day(t.expires_at)}` : 'udløber aldrig',
    t.last_used_at ? `brugt ${day(t.last_used_at)}` : 'aldrig brugt',
  ].join(' · ');
}

/** The Danish line under a refused token change. */
export function tokenErrorText(cause: unknown): string {
  if (cause instanceof ApiRequestError) {
    if (cause.status === 400) return 'Giv tokenet et navn.';
    if (cause.status === 403) return 'Tokens styres fra en logget-ind session.';
    if (cause.status === 404) return 'Tokenet findes ikke længere.';
  }
  return 'Ændringen kunne ikke gemmes.';
}
